/**
 * Die vier Vergabemodelle und ihre Organigramm-Struktur.
 *
 * 1:1 portiert aus `sige-plan/src/config/org-models.ts` der Quell-App. Nur
 * die Typherkunft ist getauscht (`./types` statt `../types/sigeplan`) — die
 * Texte sind unveraendert, weil sie fachlich abgenommen sind und ein
 * Umformulieren beim Portieren nur unbemerkte Abweichungen erzeugt.
 *
 * WARUM diese Datei ueberhaupt existiert: das Vergabemodell entscheidet, wer
 * im Organigramm weisungsbefugt ist und wer nur beteiligt. `metadata.projectType`
 * ist KEIN Ersatz — der traegt 'sigeko'|'hse' und sagt nichts ueber das
 * Vertragsverhaeltnis. Deshalb ist `handbuecher.org_modell` NULL-bar und ohne
 * Vorgabewert: ein geratenes Modell zeichnet ein falsches Organigramm, und ein
 * falsches Organigramm sieht richtig aus.
 *
 * Reine Daten — kein DOM, kein React, keine Supabase.
 */

import type { BeteiligterRolle, OrgModell } from './types';

/**
 * Ein Kasten des Organigramms, in der Reihenfolge von oben nach unten.
 *
 * Die Tiefe wird als ZAHL gefuehrt und nicht als vorgesetzte Einrueckung im
 * Text: nur so kann der Empfaenger die Verbindungslinien selbst zeichnen,
 * statt sie aus Zeichen zusammenzusetzen.
 */
export interface OrgStrukturKnoten {
    /** Einrueckungsstufe; 0 ist die Wurzel („Projekt"). */
    tiefe: number;
    label: string;
}

export interface OrgModellKonfig {
    id: OrgModell;
    label: string;
    beschreibung: string;
    /** Welche Rollenkaesten das Modell zeichnet. */
    beteiligteRollen: BeteiligterRolle[];
    /** Unter welchem Kasten die Gewerke haengen. */
    gewerkeUnter: 'bauherr' | 'gu' | 'gue' | 'tu';
    /** Traegt der Auftragnehmer die Planung selbst? Nur beim TU. */
    hatEigenePlanung: boolean;
    /**
     * Die Hierarchie als Knoten — NICHT als fertig gemalte Textzeilen.
     *
     * Hier standen einmal Zeilen wie '  └─ Bauherr'. Das Handbuch-PDF setzt mit
     * den Standardschriften von jsPDF, und die koennen ausschliesslich CP1252:
     * ein Rahmenzeichen kippt die GANZE Zeile in UTF-16, und auf dem Blatt
     * stand dann '%%  B a u h e r r' statt des Baums (Herleitung in
     * `services/pdf/winAnsi.ts`). Die Verbindungslinien zeichnet jetzt der
     * Generator selbst — so wie das Organigramm am Bildschirm, das seine
     * Linien ebenfalls zeichnet und keine Zeichen malt.
     */
    struktur: OrgStrukturKnoten[];
    erklaerung: string;
}

export const ORG_MODELLE: Record<OrgModell, OrgModellKonfig> = {
    einzelvergabe: {
        id: 'einzelvergabe',
        label: 'Einzelvergabe',
        beschreibung: 'Bauherr vergibt jedes Gewerk einzeln — maximale Kontrolle, hoher Steuerungsaufwand',
        beteiligteRollen: ['planer', 'sigeko', 'bauleitung'],
        gewerkeUnter: 'bauherr',
        hatEigenePlanung: false,
        struktur: [
            { tiefe: 0, label: 'Projekt' },
            { tiefe: 1, label: 'Bauherr' },
            { tiefe: 2, label: 'Planer / Architekt' },
            { tiefe: 2, label: 'SiGeKo' },
            { tiefe: 2, label: 'Bauleitung' },
            { tiefe: 2, label: 'Gewerk 1 (direkt beauftragt)' },
            { tiefe: 2, label: 'Gewerk 2 (direkt beauftragt)' },
            { tiefe: 2, label: 'Gewerk n (direkt beauftragt)' },
        ],
        erklaerung: 'Der Bauherr schließt mit jedem Gewerk einen eigenen Vertrag. Er behält die volle Kontrolle über alle Einzelvergaben, muss aber auch die gesamte Koordination selbst organisieren. Planer, Bauleitung und SiGeKo werden separat beauftragt.',
    },
    gu: {
        id: 'gu',
        label: 'Generalunternehmer (GU)',
        beschreibung: 'GU übernimmt die Ausführung aller Gewerke — Planung bleibt beim Bauherr/Planer',
        beteiligteRollen: ['planer', 'sigeko', 'gu'],
        gewerkeUnter: 'gu',
        hatEigenePlanung: false,
        struktur: [
            { tiefe: 0, label: 'Projekt' },
            { tiefe: 1, label: 'Bauherr' },
            { tiefe: 2, label: 'Planer / Architekt' },
            { tiefe: 2, label: 'SiGeKo' },
            { tiefe: 2, label: 'Generalunternehmer (GU)' },
            { tiefe: 3, label: 'Eigene Kräfte (z.B. Rohbau)' },
            { tiefe: 3, label: 'Nachunternehmer Elektro' },
            { tiefe: 3, label: 'Nachunternehmer Sanitär' },
            { tiefe: 3, label: 'Nachunternehmer n' },
        ],
        erklaerung: 'Der GU übernimmt die gesamte Bauausführung. Er führt Teile selbst aus (eigene Kräfte) und vergibt den Rest an Nachunternehmer. Die Planung bleibt beim Bauherr bzw. seinem Planer. Der Bauherr hat nur einen Vertragspartner für die Ausführung.',
    },
    gue: {
        id: 'gue',
        label: 'Generalübernehmer (GÜ)',
        beschreibung: 'GÜ koordiniert alles, führt nichts selbst aus — vergibt komplett an Nachunternehmer',
        beteiligteRollen: ['planer', 'sigeko', 'gue'],
        gewerkeUnter: 'gue',
        hatEigenePlanung: false,
        struktur: [
            { tiefe: 0, label: 'Projekt' },
            { tiefe: 1, label: 'Bauherr' },
            { tiefe: 2, label: 'Planer / Architekt' },
            { tiefe: 2, label: 'SiGeKo' },
            { tiefe: 2, label: 'Generalübernehmer (GÜ)' },
            { tiefe: 3, label: 'Nachunternehmer Rohbau' },
            { tiefe: 3, label: 'Nachunternehmer Elektro' },
            { tiefe: 3, label: 'Nachunternehmer Sanitär' },
            { tiefe: 3, label: 'Nachunternehmer n' },
        ],
        erklaerung: 'Der GÜ koordiniert und vergibt alle Bauleistungen, führt aber selbst KEINE Arbeiten aus. Alle Gewerke werden an Nachunternehmer vergeben. Der GÜ haftet gegenüber dem Bauherr für die Gesamtleistung seiner Nachunternehmer.',
    },
    tu: {
        id: 'tu',
        label: 'Totalunternehmer (TU)',
        beschreibung: 'TU übernimmt Planung UND Ausführung — Bauherr definiert nur die Anforderungen',
        beteiligteRollen: ['sigeko', 'tu'],
        gewerkeUnter: 'tu',
        hatEigenePlanung: true,
        struktur: [
            { tiefe: 0, label: 'Projekt' },
            { tiefe: 1, label: 'Bauherr' },
            { tiefe: 2, label: 'SiGeKo' },
            { tiefe: 2, label: 'Totalunternehmer (TU)' },
            { tiefe: 3, label: 'Eigene Planung' },
            { tiefe: 3, label: 'Eigene Kräfte / Gewerke' },
            { tiefe: 3, label: 'Nachunternehmer n' },
        ],
        erklaerung: 'Der TU übernimmt sowohl die Planung als auch die komplette Ausführung. Der Bauherr definiert nur seine Anforderungen und hat einen einzigen Vertragspartner. Kein separater Planer/Architekt nötig — der TU plant und baut alles.',
    },
};

/** Reihenfolge fuer Auswahlfelder. Getrennt vom Record, damit die Anzeige
 *  nicht von der Schluesselreihenfolge eines Objekts abhaengt. */
export const ORG_MODELL_REIHENFOLGE: OrgModell[] = ['einzelvergabe', 'gu', 'gue', 'tu'];

/** Anzeigename der Rollen. Steht hier und nicht in der Komponente, damit
 *  Bildschirm und PDF denselben Wortlaut verwenden. */
export const ROLLE_LABEL: Record<BeteiligterRolle, string> = {
    planer: 'Planer / Architekt',
    sigeko: 'SiGeKo',
    bauleitung: 'Bauleitung',
    gu: 'Generalunternehmer (GU)',
    gue: 'Generalübernehmer (GÜ)',
    tu: 'Totalunternehmer (TU)',
    tragwerksplaner: 'Tragwerksplaner',
    bodengutachter: 'Bodengutachter',
    sonstige: 'Sonstige Beteiligte',
};

/**
 * Konfiguration zu einem — moeglicherweise noch nicht bestimmten — Modell.
 *
 * Gibt `null` zurueck statt auf ein Modell auszuweichen. Ein Vorgabewert waere
 * eine Behauptung ueber das Vertragsverhaeltnis, die niemand getroffen hat;
 * das Organigramm bleibt dann lieber leer als falsch.
 */
export const orgModellKonfig = (modell: OrgModell | null | undefined): OrgModellKonfig | null =>
    modell ? ORG_MODELLE[modell] ?? null : null;

/** Anzeigename eines Modells, mit ehrlichem Text fuer „noch nicht bestimmt". */
export const orgModellLabel = (modell: OrgModell | null | undefined): string =>
    orgModellKonfig(modell)?.label ?? 'Vergabemodell noch nicht bestimmt';
